import { useState, useMemo, useRef, useLayoutEffect } from "react"
import * as React from 'react'
import { getIframeSrc } from '../utils/configurator-utils.js'
import { useOV25UI } from "../contexts/ov25-ui-context.js"
import { cn, getProductGalleryImages, resolveImageUrl } from "../lib/utils.js"
import ConfiguratorViewControls from './ConfiguratorViewControls.js'
import Snap2ViewControls from './Snap2ViewControls.js'
import { Ov25ShadowHost } from './Ov25ShadowHost.js'


export const IframeContainer = () => {
    // Get all required data from context
    const {
        iframeRef,
        currentProduct,
        galleryIndex,
        productLink,
        apiKey,
        configurationUuid,
        bedAllowNoneQueryValue,
        diningShowAttachmentPoints,
        galleryIndexToUse,
        images: passedImages,
        isProductGalleryStacked: isStacked,
        isVariantsOpen,
        uniqueId,
        isMobile,
        deferThreeD,
        isDrawerOrDialogOpen,
        configuratorDisplayMode,
        configuratorDisplayModeMobile,
        isSnap2Mode,
        isModalOpen,
    } = useOV25UI();

    const isModalMode =
        isMobile ? configuratorDisplayModeMobile === 'modal' : configuratorDisplayMode === 'modal';
    const snap2DesktopInlineSheet =
        isSnap2Mode && !isMobile && configuratorDisplayMode === 'inline-sheet';
    const snap2DesktopModalStackedFill =
        isStacked &&
        isSnap2Mode &&
        !isMobile &&
        ((isModalMode && isModalOpen) || snap2DesktopInlineSheet);
    const snap2MobileDrawerOpen =
        isSnap2Mode &&
        isMobile &&
        isDrawerOrDialogOpen &&
        !isModalMode &&
        configuratorDisplayModeMobile !== 'inline';
    const iframeRadiusClass = snap2MobileDrawerOpen
        ? 'ov:rounded-none'
        : 'ov:rounded-[var(--ov25-configurator-iframe-border-radius)]';

    const controlsContainerRef = useRef<HTMLDivElement>(null);
    const dummyIframeRef = useRef<HTMLIFrameElement>(null);

    useLayoutEffect(() => {
        const el = dummyIframeRef.current;
        if (!el) return;
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('height', '0', 'important');
        el.style.setProperty('width', '0', 'important');
        el.style.setProperty('border', '0', 'important');
        el.style.setProperty('margin', '0', 'important');
        el.style.setProperty('padding', '0', 'important');
        el.style.setProperty('position', 'absolute', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
    }, []);

    const hasCutout = !!(currentProduct?.metadata as any)?.cutoutImage
    const cutoutFirst = hasCutout && (isMobile || !deferThreeD)
    const productImages = getProductGalleryImages(currentProduct?.metadata, { cutoutFirst })
    const images = [...(passedImages || []), ...productImages]

    // Any component-specific state remains local
    const [canSeeDimensions, setCanSeeDimensions] = useState(false);


    // Calculate showDimensionsToggle from currentProduct
    const showDimensionsToggle = !!((currentProduct as any)?.dimensionX &&
        (currentProduct as any)?.dimensionY &&
        (currentProduct as any)?.dimensionZ);

    // Get background color from CSS variable and ensure it's hex
    const hexBgColor = useMemo(() => {
        const root = document.documentElement;
        const cssValue = getComputedStyle(root).getPropertyValue('--ov25-configurator-iframe-background-color').trim();

        if (!cssValue) return '#f6f6f6'; // default matching --ov25-secondary-background-color in globals.css

        // If already hex format, return it
        if (cssValue.startsWith('#')) {
            return cssValue;
        }

        // Convert to hex using computed style
        const tempDiv = document.createElement('div');
        tempDiv.style.backgroundColor = cssValue;
        document.body.appendChild(tempDiv);
        const rgb = getComputedStyle(tempDiv).backgroundColor;
        document.body.removeChild(tempDiv);

        const match = rgb.match(/\d+/g);
        if (match && match.length >= 3) {
            return `#${[match[0], match[1], match[2]].map(x => {
                const h = parseInt(x).toString(16);
                return h.length === 1 ? '0' + h : h;
            }).join('')}`;
        }

        return null;
    }, []);

    // Use the utility function to get the iframe src
    const iframeSrc = useMemo(() =>
        getIframeSrc(apiKey, productLink, configurationUuid, hexBgColor, bedAllowNoneQueryValue, diningShowAttachmentPoints),
        [productLink, apiKey, configurationUuid, hexBgColor, bedAllowNoneQueryValue, diningShowAttachmentPoints]);

    const isStackedStyles = cn(
        snap2DesktopModalStackedFill
            ? "ov:relative ov:h-full ov:min-h-0 ov:w-full ov:max-h-full ov:overflow-hidden ov:z-[3]"
            : "ov:relative ov:aspect-square ov:md:aspect-[3/2] ov:2xl:aspect-video ov:overflow-hidden ov:z-[3]",
        iframeRadiusClass,
        "ov:bg-[var(--ov25-configurator-iframe-background-color)]",
        "ov:transform-gpu ov:backface-hidden",
    )
    const isInlineStyles = cn(
        "ov:absolute ov:size-full ov:inset-0 ov:overflow-hidden ov:z-[3]",
        iframeRadiusClass,
        "ov:bg-[var(--ov25-configurator-iframe-background-color)]",
        "ov:transform-gpu ov:backface-hidden",
    )

    return (
        <div id="true-ov25-configurator-iframe-container"
            data-clarity-mask="true"
            className={cn(isStacked ? isStackedStyles : isInlineStyles)}>
            <iframe ref={dummyIframeRef} id="ov25-dummy-iframe" allow="camera; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; xr-spatial-tracking; fullscreen" hidden></iframe> {/* Used as bait to stop Trustpilot from hijacking our iframe. it looks for first iframe in the DOM */}
            <iframe
                data-fullscreen={isVariantsOpen}
                data-clarity-mask="true"
                ref={iframeRef}
                id={uniqueId ? `ov25-configurator-iframe-${uniqueId}` : "ov25-configurator-iframe"}
                src={iframeSrc}
                className={`ov:w-full ov:bg-transparent ov:h-full ${iframeRadiusClass} ov:transform-gpu ov:backface-hidden ${galleryIndex === galleryIndexToUse ? 'ov:block' : 'ov:ov25-controls-hidden'}`}
                allow="camera; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; xr-spatial-tracking; fullscreen"

            />
            {/* Display selected image when galleryIndex is not the 3D spin */}
            {(() => {
                const imageIndex = galleryIndex < galleryIndexToUse ? galleryIndex : galleryIndex - 1;
                const img = images[imageIndex];
                const src = img ? resolveImageUrl(img as any, 'main') : null;
                // Sheet/drawer: poster sits in a bad stacking context vs variant UI (esp. deferThreeD); hide until closed.
                // Modal: keep poster — gallery is repositioned into the modal slot with correct stacking.
                const showPoster =
                    galleryIndex !== galleryIndexToUse &&
                    !!src &&
                    (!isDrawerOrDialogOpen || isModalMode);
                return showPoster ? (
                    <img
                        id={`ov-25-configurator-product-image-${galleryIndex}`}
                        src={src}
                        alt={`Product image ${galleryIndex}`}
                        className="ov:object-cover ov:min-h-full ov:min-w-full ov:z-5 ov:absolute ov:inset-0 ov:bg-(--ov25-configurator-iframe-background-color)"
                    />
                ) : null;
            })()}

            {galleryIndex === galleryIndexToUse &&
                !(isSnap2Mode && isMobile && !isModalMode && configuratorDisplayModeMobile !== 'inline') && (
                <Ov25ShadowHost
                    ref={controlsContainerRef}
                    id={uniqueId ? `true-configurator-view-controls-container-${uniqueId}` : "true-configurator-view-controls-container"}
                    className="ov:absolute ov:inset-0 ov:z-101 ov:w-full ov:h-full ov:pointer-events-none"
                >
                    {isSnap2Mode ? <Snap2ViewControls /> : <ConfiguratorViewControls />}
                </Ov25ShadowHost>
            )}

            {/* Container for Toaster portal - must be inside fullscreen element */}
            <div id={uniqueId ? `true-toaster-container-${uniqueId}` : "true-toaster-container"} className="ov:absolute ov:inset-0 ov:w-full ov:h-full ov:pointer-events-none ov:z-999"></div>

        </div>
    )
}
